import { useLocation } from "react-router-dom";

import { blogPosts, isPreviewableBlogPost } from "@/content/blog";
import { getCapabilityBySlug } from "@/content/capabilities";
import { getIndustryBySlug } from "@/content/industries";
import { getServiceBySlug, isListableService } from "@/content/services";
import { routes } from "@/app/common/functions/routes/routes";

export type Breadcrumb = {
  label: string;
  href?: string;
};

// Derives the trail from the URL alone, reading labels from content records.
// Index pages return an empty array ([]) so Breadcrumbs renders null, preserving
// strict navigation hierarchy without fake "... / Index" spacers.
export function useBreadcrumbs(): Breadcrumb[] {
  const { pathname } = useLocation();
  const [section, slug] = pathname.split("/").filter(Boolean);

  if (!section) return [];

  switch (section) {
    case "about":
    case "contact":
    case "privacy":
      return [];
    case "capabilities":
      if (!slug) return [];
      return [
        { label: "Capabilities", href: routes.capabilities.index },
        { label: getCapabilityBySlug(slug)?.title ?? "Not Found" },
      ];
    case "industries":
      if (!slug) return [];
      return [
        { label: "Industries", href: routes.industries.index },
        { label: getIndustryBySlug(slug)?.title ?? "Not Found" },
      ];
    case "services": {
      if (!slug) return [];
      const service = getServiceBySlug(slug);
      return [
        { label: "Services", href: routes.services.index },
        {
          label:
            service && isListableService(service) ? service.title : "Not Found",
        },
      ];
    }
    case "blog": {
      if (!slug) return [];
      const post = blogPosts.find((p) => p.slug === slug);
      return [
        { label: "Blog", href: routes.blog.index },
        {
          label: post && isPreviewableBlogPost(post) ? post.title : "Not Found",
        },
      ];
    }
    default:
      return [];
  }
}
